// APL Penalty Cup — shared client helpers (sounds, share, trophy defs, gating)
import { useEffect, useRef, useState } from "react";

const BASE = "/games/penalty-cup";

// Release flag — keep the Penalty Cup ADMIN-ONLY (web + native) until launch.
// Flip to true to go live (live-server deploy, no rebuild). When false, regular
// users are gated everywhere and the promo banner/entry points are hidden for them.
export const MINICUP_PUBLIC = true;

export const ASSETS = {
  stadium: `${BASE}/assets/stadium.png`,
  keeper: {
    idle: `${BASE}/assets/keeper_idle.png`,
    dive_left: `${BASE}/assets/keeper_dive_left.png`,
    dive_right: `${BASE}/assets/keeper_dive_right.png`,
    catch_high: `${BASE}/assets/keeper_catch_high.png`,
    catch_low: `${BASE}/assets/keeper_catch_low.png`,
  },
};

// Penalty Roulette: 6 goal zones (3 cols × 2 rows) → keeper pose for a save.
export const ZONES = ["TL", "TC", "TR", "BL", "BC", "BR"];
export const ZONE_KEEPER = {
  TL: "dive_left", TC: "catch_high", TR: "dive_right",
  BL: "dive_left", BC: "catch_low", BR: "dive_right",
};

const SOUND_SRC = {
  kick: `${BASE}/sounds/kick.wav`,
  goal: `${BASE}/sounds/goal.wav`,
  save: `${BASE}/sounds/save.wav`,
  music: `${BASE}/sounds/music.wav`,
};

const MUTE_KEY = "apl_minicup_muted";
export const isMuted = () => localStorage.getItem(MUTE_KEY) === "1";
export const setMuted = (v) => localStorage.setItem(MUTE_KEY, v ? "1" : "0");

// Lightweight sound manager — lazily creates Audio, respects mute.
export function createSound() {
  const cache = {};
  const get = (k) => {
    if (!cache[k]) {
      const a = new Audio(SOUND_SRC[k]);
      a.preload = "auto";
      if (k === "music") { a.loop = true; a.volume = 0.4; }
      cache[k] = a;
    }
    return cache[k];
  };
  return {
    play(k) {
      if (isMuted()) return;
      try { const a = get(k); a.currentTime = 0; a.play().catch(() => {}); } catch {}
    },
    startMusic() {
      if (isMuted()) return;
      try { const a = get("music"); a.play().catch(() => {}); } catch {}
    },
    stopMusic() {
      try { const a = cache["music"]; if (a) { a.pause(); a.currentTime = 0; } } catch {}
    },
    stopAll() {
      Object.values(cache).forEach((a) => { try { a.pause(); a.currentTime = 0; } catch {} });
    },
    preload() { Object.keys(SOUND_SRC).forEach(get); },
  };
}

// Penalty Roulette timing + difficulty ramp (countdown shrinks as rounds grow)
export const ROULETTE = {
  ballTravelMs: 400,
  revealMs: 600,
  goalFlashMs: 450,
  saveHoldMs: 1000,
  noShotMs: 550,
};
export function countdownForRound(round) {
  if (round <= 5) return 2000;
  if (round <= 10) return 1500;
  if (round <= 20) return 1000;
  return 500;
}

// Trophy definitions (mirror of backend)
export const TROPHY_DEFS = [
  { category: "top_striker", title: "Top Striker", desc: "Goals in a single match", metric: "best_match", tiers: [10, 20, 30, 40] },
  { category: "match_star", title: "Match Star", desc: "Total goals across all matches", metric: "total_goals", tiers: [100, 250, 500, 1000] },
  { category: "globetrotter", title: "Globetrotter", desc: "Different teams played", metric: "teams_played", tiers: [10, 24, 36, 48] },
  { category: "goal_machine", title: "Goal Machine", desc: "Career total goals", metric: "total_goals", tiers: [500, 1000, 2500, 5000] },
  { category: "streak_master", title: "Streak Master", desc: "Consecutive matches scoring 15+", metric: "streak_best", tiers: [3, 5, 10, 20] },
  { category: "tournament_champ", title: "Tournament Champ", desc: "Career goal milestones", metric: "total_goals", tiers: [50, 200, 750, 2000] },
];
export const TIER_NAMES = ["Bronze", "Silver", "Gold", "Diamond"];
export const TIER_COLORS = ["#cd7f32", "#c0c0c0", "#f5c542", "#7ee8fa"];

// Native share with web fallback (matches existing app pattern)
export async function shareGame(text) {
  try {
    if (navigator.share) { await navigator.share({ title: "APL Penalty Cup", text }); return true; }
  } catch { /* user cancelled */ return false; }
  try { await navigator.clipboard.writeText(text); return "copied"; } catch { return false; }
}

export function useNative() {
  const [native, setNative] = useState(false);
  useEffect(() => {
    (async () => {
      try { const { Capacitor } = await import("@capacitor/core"); setNative(Capacitor.isNativePlatform()); }
      catch { setNative(false); }
    })();
  }, []);
  return native;
}

export const STORE = {
  ios: "https://apps.apple.com/app/id6760789788",
  android: "https://play.google.com/store/apps/details?id=com.apnapremierleague.app",
};
